"use client";

import { useMemo, useRef, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { type Columns, zipColumns } from "@/components/tools/example-columns";
import { DbqDocumentCard } from "@/components/tools/lit-essays/dbq-document-card";
import { FrameList } from "@/components/tools/lit-essays/frame-list";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  blankDocuments,
  buildDbqOutline,
  buildDbqThesisFrames,
  COMPLEXITY_MOVES,
  type ComplexityMove,
  DBQ_THESIS_FRAMES,
  type DbqDocument,
  type DbqGroup,
  type DbqInput,
  MAX_GROUPS,
  MIN_GROUPS,
} from "@/lib/tool-kit/lit-essays/dbq-outline";

/** English strings. A translated content file overrides these through `ui`. */
export const DBQ_OUTLINE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  promptHeading: "The prompt",
  promptLabel: "DBQ prompt",
  promptPlaceholder:
    "Paste the question, e.g. “Evaluate the extent to which … changed from … to …”",
  introHeading: "Introduction",
  contextLabel: "Contextualization — a broader historical context",
  contextPlaceholder:
    "Describe events, developments, or processes before, during, or after the time frame of the prompt.",
  contextHint:
    "Needs more than a phrase. Save a different specific example for “evidence beyond the documents”.",
  thesisLabel: "Thesis — a claim with a line of reasoning",
  thesisPlaceholder:
    "Answer the prompt and give your reasons or categories. Do not restate the question.",
  framesHeading: "Thesis frames",
  framesIntro:
    "Fill-in-the-blank frames that work your paragraph categories in — honest templates, no AI.",
  framesNeed: "Name your paragraph groups to see thesis frames.",
  frameLabels: [
    "Overall claim",
    "Claim with a counterpoint",
    "Change over time",
  ],
  useAsThesis: "Use as my thesis",
  copy: "Copy",
  copied: "Copied!",
  groupsHeading: "Body paragraphs — group the documents by argument",
  groupLegend: "Body paragraph {n}",
  groupNameLabel: "Category (what the documents have in common)",
  groupNamePlaceholder: "e.g. Expanded federal responsibility",
  groupClaimLabel: "Topic sentence — the paragraph's claim",
  groupClaimPlaceholder:
    "One sentence that supports your thesis and names the category.",
  addGroup: "Add a body paragraph",
  removeGroup: "Remove",
  removeGroupLabel: "Remove body paragraph {n}",
  documentsHeading: "The documents",
  documentsHint:
    "Describe each document in your own words — quoting alone does not count. Assign it to a paragraph, and choose a sourcing feature for at least two.",
  card: {
    documentLabel: "Document {n}",
    groupLabel: "Body paragraph it supports",
    noGroup: "Not placed yet",
    groupOption: "Paragraph {n}: {category}",
    groupUnnamed: "(unnamed)",
    summaryLabel: "What it says — in your own words",
    summaryPlaceholder:
      "Describe the content and how it relates to the prompt.",
    sourcingLabel: "Sourcing — feature you will analyze",
    sourcingNone: "None yet",
    sourcingLabels: {
      pov: "Point of view",
      purpose: "Purpose",
      situation: "Historical situation",
      audience: "Audience",
    },
    noteLabel: "Explain how or why it matters to your argument",
    notePlaceholder:
      "Not just who wrote it: how that shapes what the document shows.",
  },
  outsideHeading: "Evidence beyond the documents",
  outsideLabel: "Specific evidence not in the documents — one per line",
  outsidePlaceholder:
    "e.g. The Wagner Act (1935) protected workers’ right to organize, expanding federal regulation of labor relations.",
  outsideHint:
    "Name it and say what it was. It must differ from your contextualization example.",
  complexityHeading: "Complexity",
  complexityMoveLabel: "How will you show complex understanding?",
  complexityNone: "Not decided yet",
  complexityNoteLabel: "Your plan, in a sentence or two",
  complexityNotePlaceholder:
    "Say what you will qualify, compare, or connect, and which evidence you will use.",
  complexityMoves: {
    perspectives: "Explain multiple themes or perspectives to show nuance",
    multiple:
      "Explain multiple causes or effects, similarities or differences, or continuities or changes",
    both: "Explain both cause and effect, both similarity and difference, or both continuity and change",
    connections:
      "Make insightful connections within and across periods or regions",
    sevenDocuments:
      "Use all seven documents effectively to support the argument",
    sourcingFour:
      "Explain how the sourcing of at least four documents supports the argument",
    differentViews:
      "Use documents and outside evidence to show different perspectives",
  },
  fileName: "dbq-outline",
  document: {
    heading: "Your DBQ outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the outline has your content.",
  },
  docTitle: "DBQ Outline",
  subtitle: "AP History document-based question · seven-point rubric",
  headings: {
    prompt: "Prompt",
    introduction: "Introduction",
    body: "Body paragraph {n}: {category}",
    unplaced: "Documents not yet placed in a paragraph",
    outside: "Evidence beyond the documents",
    complexity: "Complexity",
    rubric: "Planning check against the seven-point rubric",
  },
  items: {
    context: "Contextualization",
    thesis: "Thesis",
    claim: "Topic sentence",
    documents: "Documents",
    link: "Link",
    move: "Approach",
  },
  documentLabel: "Document {n}",
  sourcingLabels: {
    pov: "point of view",
    purpose: "purpose",
    situation: "historical situation",
    audience: "audience",
  },
  sourcingLine: "Sourcing ({feature}): {note}",
  prompts: {
    prompt:
      "Paste the DBQ prompt here, and underline the task word and the time frame.",
    context:
      "Contextualization — describe a broader event, development, or process before, during, or after the time frame. More than a phrase.",
    thesis:
      "Thesis — answer the prompt with a claim and a line of reasoning or categories. Do not restate the question.",
    claim:
      "Topic sentence — one sentence that supports the thesis for this group of documents.",
    noDocuments: "Assign at least two documents to this paragraph.",
    describe:
      "{document} — describe what it says in your own words; do not just quote it.",
    explain:
      "Sourcing — explain how or why the {feature} matters to your argument, not only who wrote it.",
    outside:
      "Add at least one specific piece of evidence from outside the documents, with what it was and why it matters.",
    complexity:
      "Choose how you will show complex understanding, and write the sentence or two that does it.",
    link: "Link — tie the paragraph back to the thesis.",
    unplaced: "Place these documents in a paragraph.",
  },
  guidance: {
    context:
      "Contextualization and the thesis may appear anywhere in the essay. The thesis must be in one place, in the introduction or the conclusion.",
    outside:
      "One described piece earns the point. It may appear anywhere in the essay, but must differ from your contextualization example.",
    unplaced:
      "Documents you describe but have not placed do not count toward the second evidence point.",
  },
  rubricNote:
    "A planning check against the AP History DBQ rubric (seven points), not a score. The length tests are rough proxies for “more than a phrase”; a reader scoring the essay decides.",
  plannedLine: "Your outline is set up for {planned} of 7 points.",
  rubric: {
    thesis:
      "Thesis/claim (1 point): a claim that answers the prompt, not a restatement",
    contextualization:
      "Contextualization (1 point): a broader context, more than a phrase",
    evidenceOne:
      "Evidence from the documents (1st point): content of at least three documents described",
    evidenceTwo:
      "Evidence from the documents (2nd point): at least four documents used to support an argument",
    beyond:
      "Evidence beyond the documents (1 point): one specific piece, described",
    sourcing:
      "Sourcing (1 point): for at least two documents, how or why point of view, purpose, situation, or audience matters",
    complexity:
      "Complexity (1 point): complex understanding built into the argument",
  },
  and: "and",
  categoryStand: "[category {n}]",
  example: {
    prompt:
      "Evaluate the extent to which the New Deal changed the role of the federal government in the American economy from 1933 to 1939.",
    context:
      "The Great Depression, which began with the 1929 stock market crash, left roughly a quarter of the workforce unemployed by 1933 and overwhelmed local and private relief, after a decade of laissez-faire policy in the 1920s.",
    thesis:
      "The New Deal changed the role of the federal government in the economy to a great extent, because Washington took on responsibility for economic security and recovery, although opposition from business leaders and the Supreme Court limited how far the change went.",
    outside:
      "The Civilian Conservation Corps (1933) put unemployed young men to work on conservation projects, a direct federal jobs program.\nThe Wagner Act (1935) protected workers’ right to organize and bargain collectively, expanding federal regulation of labor relations.",
    complexityMove: "multiple",
    complexityNote:
      "I will qualify the argument by showing that the Court’s rejection of the National Industrial Recovery Act redirected the New Deal rather than ending it, and that Social Security excluded many agricultural and domestic workers.",
  },
  exampleGroups: {
    name: ["Expanded federal responsibility", "Limits and opposition"],
    claim: [
      "The New Deal made the federal government responsible for relief, recovery, and economic security.",
      "Opposition from business leaders and the courts limited the change and shaped what came next.",
    ],
  } as Columns<"name" | "claim">,
  exampleDocuments: {
    summary: [
      "Franklin Roosevelt’s First Inaugural Address (1933) asks Congress for broad executive power to fight the emergency, as if the nation were invaded.",
      "The Social Security Act (1935) created old-age insurance funded by payroll taxes and a federal-state system of unemployment compensation.",
      "In Schechter Poultry Corp. v. United States (1935) the Supreme Court unanimously struck down the National Industrial Recovery Act’s codes as an overreach of federal power.",
      "The American Liberty League, formed in 1934, argued that the New Deal threatened individual liberty and property rights.",
      "The Tennessee Valley Authority Act (1933) created a federal corporation to build dams, control floods, and generate electric power.",
    ],
    sourcingNote: [
      "Roosevelt’s purpose was to reassure a frightened public and justify extraordinary federal power, which shows how the crisis made a larger federal role acceptable.",
      "Passed amid the Depression and pressure from movements such as the Townsend Plan, the act shows the government answering demands for security that charity and the states could not meet.",
      "",
      "The League was backed by prominent business leaders, so its objection to regulation reflects the interests of those with the most to lose, which suggests the limits came from organized opposition.",
      "",
    ],
  } as Columns<"summary" | "sourcingNote">,
} as const;

/**
 * The non-text half of each example document, by row. Kept out of the strings because a
 * translated `ui` block holds only text: the group number and the sourcing feature are
 * tokens the widget reads, not words a translator renders.
 */
const EXAMPLE_DOCUMENT_FACTS: readonly Pick<
  DbqDocument,
  "group" | "sourcing"
>[] = [
  { group: 1, sourcing: "purpose" },
  { group: 1, sourcing: "situation" },
  { group: 2, sourcing: "" },
  { group: 2, sourcing: "pov" },
  { group: 1, sourcing: "" },
];

type Strings = typeof DBQ_OUTLINE_GENERATOR_STRINGS;

/** The example's body-paragraph groups, ids from 1, English filling any short column. */
export function dbqExampleGroups(strings: Strings): DbqGroup[] {
  return zipColumns(
    DBQ_OUTLINE_GENERATOR_STRINGS.exampleGroups,
    strings.exampleGroups
  ).map((group, index) => ({ id: index + 1, ...group }));
}

/** The example's documents by row; the booklet ids and blanks come from the caller. */
export function dbqExampleDocuments(
  strings: Strings
): Pick<DbqDocument, "group" | "sourcing" | "summary" | "sourcingNote">[] {
  return zipColumns(
    DBQ_OUTLINE_GENERATOR_STRINGS.exampleDocuments,
    strings.exampleDocuments
  ).map((text, index) => ({ ...EXAMPLE_DOCUMENT_FACTS[index], ...text }));
}

type TextKey = "prompt" | "context" | "thesis" | "outside" | "complexityNote";

const BLANK: Pick<DbqInput, TextKey | "complexityMove"> = {
  prompt: "",
  context: "",
  thesis: "",
  outside: "",
  complexityMove: "",
  complexityNote: "",
};

const BLANK_GROUPS: DbqGroup[] = [
  { id: 1, name: "", claim: "" },
  { id: 2, name: "", claim: "" },
];

export default function DbqOutlineGeneratorWidget() {
  const strings = useToolUi(DBQ_OUTLINE_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const nextGroup = useRef(MIN_GROUPS);
  const [fields, setFields] = useState(BLANK);
  const [groups, setGroups] = useState<DbqGroup[]>(BLANK_GROUPS);
  const [documents, setDocuments] = useState<DbqDocument[]>(blankDocuments);
  const input: DbqInput = useMemo(
    () => ({ ...fields, groups, documents }),
    [fields, groups, documents]
  );
  const doc = useMemo(() => buildDbqOutline(input, strings), [input, strings]);
  const frames = buildDbqThesisFrames(
    groups,
    strings.and,
    (n) => t(strings.categoryStand, { n: fmt(n) }),
    DBQ_THESIS_FRAMES
  );
  const update = (key: TextKey) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const patchGroup = (id: number, change: Partial<DbqGroup>) =>
    setGroups((previous) =>
      previous.map((group) =>
        group.id === id ? { ...group, ...change } : group
      )
    );
  const patchDocument = (id: number, change: Partial<DbqDocument>) =>
    setDocuments((previous) =>
      previous.map((entry) =>
        entry.id === id ? { ...entry, ...change } : entry
      )
    );
  const moveOptions: { value: ComplexityMove; label: string }[] = [
    { value: "", label: strings.complexityNone },
    ...COMPLEXITY_MOVES.map((value) => ({
      value,
      label: strings.complexityMoves[value],
    })),
  ];

  function removeGroup(id: number) {
    setGroups((previous) => previous.filter((group) => group.id !== id));
    setDocuments((previous) =>
      previous.map((entry) =>
        entry.group === id ? { ...entry, group: 0 } : entry
      )
    );
  }

  function loadExample() {
    const exampleGroups = dbqExampleGroups(strings);
    const exampleDocuments = dbqExampleDocuments(strings);
    setFields({ ...BLANK, ...strings.example });
    setGroups(exampleGroups);
    nextGroup.current = exampleGroups.length;
    setDocuments(
      blankDocuments().map((entry, index) => ({
        ...entry,
        ...(exampleDocuments[index] ?? {}),
      }))
    );
  }

  function clear() {
    setFields(BLANK);
    setGroups(BLANK_GROUPS);
    nextGroup.current = MIN_GROUPS;
    setDocuments(blankDocuments());
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6 print:hidden">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button className={BUTTON_SECONDARY} onClick={clear} type="button">
            {strings.clear}
          </button>
        </div>
        <Panel title={strings.promptHeading}>
          <TextAreaField
            id="dbq-prompt"
            label={strings.promptLabel}
            onChange={update("prompt")}
            placeholder={strings.promptPlaceholder}
            prose
            rows={2}
            value={fields.prompt}
          />
        </Panel>
        <Panel title={strings.introHeading}>
          <TextAreaField
            hint={strings.contextHint}
            id="dbq-context"
            label={strings.contextLabel}
            onChange={update("context")}
            placeholder={strings.contextPlaceholder}
            prose
            rows={3}
            value={fields.context}
          />
          <TextAreaField
            id="dbq-thesis"
            label={strings.thesisLabel}
            onChange={update("thesis")}
            placeholder={strings.thesisPlaceholder}
            prose
            rows={3}
            value={fields.thesis}
          />
        </Panel>
        <section className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.framesHeading}</h2>
          <FrameList
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            frames={frames}
            intro={strings.framesIntro}
            labels={strings.frameLabels}
            need={strings.framesNeed}
            onUse={update("thesis")}
            useLabel={strings.useAsThesis}
          />
        </section>
        <section className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.groupsHeading}</h2>
          {groups.map((group, index) => (
            <fieldset className={`${CARD} space-y-4`} key={group.id}>
              <legend className="px-1 font-medium text-foreground text-sm">
                {t(strings.groupLegend, { n: fmt(index + 1) })}
              </legend>
              <TextField
                id={`dbq-group-name-${group.id}`}
                label={strings.groupNameLabel}
                onChange={(name) => patchGroup(group.id, { name })}
                placeholder={strings.groupNamePlaceholder}
                value={group.name}
              />
              <TextAreaField
                id={`dbq-group-claim-${group.id}`}
                label={strings.groupClaimLabel}
                onChange={(claim) => patchGroup(group.id, { claim })}
                placeholder={strings.groupClaimPlaceholder}
                prose
                rows={2}
                value={group.claim}
              />
              {groups.length > MIN_GROUPS ? (
                <button
                  aria-label={t(strings.removeGroupLabel, {
                    n: fmt(index + 1),
                  })}
                  className={BUTTON_SMALL}
                  onClick={() => removeGroup(group.id)}
                  type="button"
                >
                  {strings.removeGroup}
                </button>
              ) : null}
            </fieldset>
          ))}
          {groups.length < MAX_GROUPS ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() => {
                nextGroup.current += 1;
                setGroups((previous) => [
                  ...previous,
                  { id: nextGroup.current, name: "", claim: "" },
                ]);
              }}
              type="button"
            >
              {strings.addGroup}
            </button>
          ) : null}
        </section>
        <section className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.documentsHeading}</h2>
          <p className="text-muted-foreground text-sm">
            {strings.documentsHint}
          </p>
          {documents.map((entry) => (
            <DbqDocumentCard
              doc={entry}
              groups={groups}
              key={entry.id}
              onChange={(change) => patchDocument(entry.id, change)}
              strings={strings.card}
            />
          ))}
        </section>
        <Panel title={strings.outsideHeading}>
          <TextAreaField
            hint={strings.outsideHint}
            id="dbq-outside"
            label={strings.outsideLabel}
            onChange={update("outside")}
            placeholder={strings.outsidePlaceholder}
            prose
            rows={3}
            value={fields.outside}
          />
        </Panel>
        <Panel title={strings.complexityHeading}>
          <SelectField
            id="dbq-move"
            label={strings.complexityMoveLabel}
            onChange={(complexityMove) =>
              setFields((previous) => ({ ...previous, complexityMove }))
            }
            options={moveOptions}
            value={fields.complexityMove}
          />
          <TextAreaField
            id="dbq-complexity"
            label={strings.complexityNoteLabel}
            onChange={update("complexityNote")}
            placeholder={strings.complexityNotePlaceholder}
            prose
            rows={2}
            value={fields.complexityNote}
          />
        </Panel>
      </div>
      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="dbq"
        labels={strings.document}
      />
    </div>
  );
}
